import type { CXRuntimeContext } from "./cxRuntimeContext";
import { assertSurveyAcknowledgement } from "./cxSurveyAcknowledgement";
import axios from "axios";

import { buildCxApiUrl, CX_API_ENDPOINTS } from "../config/cxApiConfig";

// Bound stalled saves without automatically retrying an ambiguously completed write.
export const PUBLIC_SURVEY_SAVE_TIMEOUT_MS = 30_000;

export interface CXPublicRedirectTarget {
  customUrl?: string | null;
  destinationType?: "custom_url" | "google_location" | string | null;
  googleLocationId?: string | null;
  googleLocationLabel?: string | null;
  googleReviewUrl?: string | null;
  id?: string | null;
  targetUrl?: string | null;
}

export interface CXPublicSurveyResponse {
  surveyDocument?: unknown;
  runtimeContext?: CXRuntimeContext;
  appointmentCode: string;
  customerSurvey?: Record<string, unknown>;
  executionStepId: string;
  existingResponse?: {
    comments?: string | null;
    rating: number;
    ratingId: number;
    scoreMethod: "CSAT" | "NPS";
    submitted: true;
    tags?: string[] | null;
  } | null;
  journey?: {
    id?: string | null;
    journeyKey?: string | null;
    name?: string | null;
  };
  module?: {
    id?: string | null;
    moduleKey?: string | null;
    name?: string | null;
  };
  moduleRevisionId: string;
  redirect?: CXPublicRedirectTarget & {
    targets?: CXPublicRedirectTarget[] | null;
  };
  sourceEvent?: {
    id?: string;
    source?: string;
    sourceEventKey?: string;
    appointmentKey?: string;
    serviceType?: string | null;
    occurredAt?: string;
    payload?: Record<string, unknown>;
  };
  triggerEvent: string;
  triggerGroupId: string;
  testMode?: boolean;
  persistenceDisabled?: boolean;
  tipsDisabled?: boolean;
}

export interface CXPublicSurveySubmission {
  comments: string;
  rating: number;
  scoreMethod: "CSAT" | "NPS";
  tags: string[];
}

export interface CXPublicSurveySubmissionResponse {
  appointmentId: number;
  ratingId: number;
  status: "completed" | "test_completed";
  testMode?: boolean;
  persisted?: boolean;
}

export async function getPublicCXSurvey(
  appointmentCode: string,
): Promise<CXPublicSurveyResponse> {
  const response = await axios.get<CXPublicSurveyResponse>(
    buildCxApiUrl(CX_API_ENDPOINTS.PUBLIC_SURVEY(appointmentCode)),
  );

  return response.data;
}

export async function submitPublicCXSurvey(
  surveyCode: string,
  submission: CXPublicSurveySubmission,
  testMode = false,
): Promise<CXPublicSurveySubmissionResponse> {
  const response = await axios.post<CXPublicSurveySubmissionResponse>(
    buildCxApiUrl(CX_API_ENDPOINTS.PUBLIC_SURVEY(surveyCode)),
    submission,
    { timeout: PUBLIC_SURVEY_SAVE_TIMEOUT_MS },
  );

  assertSurveyAcknowledgement(response.data, testMode);
  return response.data;
}

export async function trackPublicCXSurveyReviewLinkOpen(
  surveyCode: string,
): Promise<void> {
  await axios.post(
    buildCxApiUrl(CX_API_ENDPOINTS.PUBLIC_SURVEY_REVIEW_LINK_OPEN(surveyCode)),
  );
}

export async function trackPublicCXSurveyLinkOpen(
  surveyCode: string,
): Promise<void> {
  await axios.post(
    buildCxApiUrl(CX_API_ENDPOINTS.PUBLIC_SURVEY_LINK_OPEN(surveyCode)),
  );
}

export async function trackPublicCXSurveyReviewClick(
  surveyCode: string,
  provider?: "google" | "yelp" | "facebook" | "bbb",
): Promise<void> {
  const url = buildCxApiUrl(CX_API_ENDPOINTS.PUBLIC_SURVEY_REVIEW_CLICK(surveyCode));
  const response = provider
    ? await axios.post<{ tracked?: boolean; testMode?: boolean }>(url, { provider })
    : await axios.post<{ tracked?: boolean; testMode?: boolean }>(url);
  if (response.data?.tracked !== true || (response.data.testMode !== undefined && response.data.testMode !== false)) {
    throw new Error("The server did not acknowledge this review click. Please try again.");
  }
}

/** Best-effort telemetry; callers isolate errors from customer actions. */
export async function trackPublicCXSurveyReviewCTAView(surveyCode: string, provider: string): Promise<void> {
  await axios.post(buildCxApiUrl(`/api/cx/v1/public/surveys/${encodeURIComponent(surveyCode)}/review-cta-views`), { provider });
}
